import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  ButtonGroup,
  Form,
  Badge,
  Tab,
  Tabs,
  Table,
  TableScroll,
  Modal,
  Alert,
  Spinner,
  ListPage,
  PageHeader,
  FilterGrid,
  FilterField,
  RowActions,
  ACTIONS_COL_WIDTH,
  EmptyValue,
  DisplayInactiveToggle,
  HiddenMatchHint,
  type PageAction,
} from '@/components/ui';
import UsersTable from './components/UsersTable';
import UserModal from './components/UserModal';
import UserHistoryModal from '../../components/UserHistoryModal';
import FiltersCard from '../../components/FiltersCard';
import ServerPagination from '../../components/ServerPagination';
import RolesTable from '../roles/components/RolesTable';
import RoleModal from '../roles/components/RoleModal';
import ClassificationModal from '../classifications/components/ClassificationModal';
import type { User } from '../../types/User';
import type { Role } from '../../types/Role';
import type { Permission } from '../../types/User';
import type { Classification } from '../../types/Employee';
import type { ServiceArea, ForemanArea } from '../../services/areasService';
import { usersService } from '../../services/usersService';
import { rolesService } from '../../services/rolesService';
import { classificationService } from '../../services/classificationService';
import { areasService } from '../../services/areasService';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { usePersistedListState } from '../../hooks/usePersistedListState';
import { confirmAction } from '@/components/ui';
import { describeActionError } from '../../utils/loadError';

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type Season = 'summer' | 'winter' | null;
type PortalFilter = 'all' | 'portal' | 'without-portal';
type UsersTab = 'employees' | 'classifications' | 'roles';

/** sessionStorage page key for this page's remembered list state (V2-024). */
const EMPLOYEES_LIST_KEY = 'employees';

interface EmployeeFilterState {
  /** Off (the default) lists active employees only (V2-075, INACT-01). */
  displayInactive: boolean;
  portal: PortalFilter;
  serviceAreaId: number | '';
  foremanAreaId: number | '';
  roleId: number | '';
}

/** The Employees list opens on active employees; Clear All returns there. */
const DEFAULT_EMPLOYEE_FILTERS: EmployeeFilterState = {
  displayInactive: false,
  portal: 'all',
  serviceAreaId: '',
  foremanAreaId: '',
  roleId: '',
};

/**
 * GET /api/users filters for the applied employee filters. Display Inactive off sends
 * status=ACTIVE; on sends no status (every employee). `withStatus: false` drops the status for
 * the hidden-match count.
 */
const employeeQueryFilters = (f: EmployeeFilterState, search: string, withStatus = true) => {
  const filters: { status?: string; hasPortalAccess?: boolean; serviceAreaId?: number; foremanAreaId?: number; roleId?: number; search?: string } = {};
  if (withStatus && !f.displayInactive) filters.status = 'ACTIVE';
  if (f.portal === 'portal') filters.hasPortalAccess = true;
  else if (f.portal === 'without-portal') filters.hasPortalAccess = false;
  if (f.serviceAreaId) filters.serviceAreaId = f.serviceAreaId;
  if (f.foremanAreaId) filters.foremanAreaId = f.foremanAreaId;
  if (f.roleId) filters.roleId = f.roleId;
  if (search) filters.search = search;
  return filters;
};

const USERS_TAB_TITLES: Record<UsersTab, string> = {
  employees: 'Employees',
  classifications: 'Classifications & Rates',
  roles: 'Roles & Permissions',
};

const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Load season from localStorage or default to null (none selected)
  const [season, setSeason] = useState<Season>(() => {
    const savedSeason = localStorage.getItem('employeeSeasonPreference');
    return (savedSeason === 'summer' || savedSeason === 'winter') ? savedSeason : null;
  });
  
  // State for season change confirmation (for master toggle)
  const [showSeasonConfirm, setShowSeasonConfirm] = useState(false);
  const [pendingSeason, setPendingSeason] = useState<'summer' | 'winter' | null>(null);
  const [bulkUpdateCount, setBulkUpdateCount] = useState(0);

  // Save season to localStorage when it changes (view preference only)
  const handleSeasonChange = (newSeason: 'summer' | 'winter') => {
    // Show confirmation if season is actually changing (and not null)
    if (newSeason !== season && newSeason !== null) {
      // Count all users that would be affected across ALL pages (all users matching current filters)
      // This uses the total count from pagination, which represents all filtered users across all pages
      const affectedCount = pagination.totalCount || users.length;
      setBulkUpdateCount(affectedCount);
      setPendingSeason(newSeason);
      setShowSeasonConfirm(true);
    }
  };

  const confirmSeasonChange = async () => {
    if (pendingSeason) {
      // Bulk update all filtered employees
      try {
        // Build filters object from current filter state
        const filters = employeeQueryFilters(employeeFilters, searchTerm);

        // Pass null for userIds to use filters instead
        const result = await usersService.bulkUpdateSeason(null, pendingSeason, filters);
        // Small delay to ensure cache is cleared
        await new Promise(resolve => setTimeout(resolve, 100));
        // Refresh users to get updated data - reset to page 1 to see all updated users
        await fetchUsers(1, true);
        setSeason(pendingSeason);
        localStorage.setItem('employeeSeasonPreference', pendingSeason);
      } catch (error) {
        console.error('Error bulk updating seasons:', error);
        // Still update the view preference even if bulk update fails
      setSeason(pendingSeason);
      localStorage.setItem('employeeSeasonPreference', pendingSeason);
      }
    }
    setShowSeasonConfirm(false);
    setPendingSeason(null);
    setBulkUpdateCount(0);
  };

  const cancelSeasonChange = () => {
    setShowSeasonConfirm(false);
    setPendingSeason(null);
    setBulkUpdateCount(0);
  };

  // Handle individual user season change
  const handleUserSeasonChange = async (user: User, newSeason: 'summer' | 'winter') => {
    try {
      await usersService.updateUserSeason(user.id, newSeason);
      // Refresh users to get updated data
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user season:', error);
    }
  };
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  // Page, page size, sort, filters and search are kept for the sign-in (V2-024, FLT-09).
  const [savedEmployeePage, setSavedEmployeePage] = usePersistedListState(EMPLOYEES_LIST_KEY, 'page', {
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({
    page: savedEmployeePage.page,
    limit: savedEmployeePage.limit,
    totalCount: 0,
    totalPages: 0
  });
  // Employees tab server-side sort
  const [userSort, setUserSort] = usePersistedListState<{ by: string | null; dir: 'asc' | 'desc' }>(
    EMPLOYEES_LIST_KEY,
    'sort',
    { by: null, dir: 'asc' }
  );
  const userSortBy = userSort.by;
  const userSortDir = userSort.dir;
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  // A failed row action (activate, delete) shows inline on the active tab (STATE-06).
  const [actionError, setActionError] = useState<string | null>(null);

  // Applied filter state (used for actual filtering), kept for the sign-in.
  const [employeeFilters, setEmployeeFilters] = usePersistedListState<EmployeeFilterState>(
    EMPLOYEES_LIST_KEY,
    'filters',
    DEFAULT_EMPLOYEE_FILTERS
  );
  const {
    displayInactive,
    portal: portalFilter,
    serviceAreaId: employeeServiceAreaId,
    foremanAreaId: employeeForemanAreaId,
    roleId: employeeRoleId,
  } = employeeFilters;
  // The search runs from the field in the filter panel's header bar (Enter, or the clear "×"),
  // within the current filters (SRCH-04); the box text is plain state until then.
  const [searchTerm, setSearchTerm] = usePersistedListState(EMPLOYEES_LIST_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(searchTerm);
  // The same search without the Display Inactive filter, for the hidden-match hint (SRCH-05).
  const [unfilteredMatchCount, setUnfilteredMatchCount] = useState<number | null>(null);

  // Employee filter state - pending values (not applied until Apply is clicked)
  const [pendingDisplayInactive, setPendingDisplayInactive] = useState(displayInactive);
  const [pendingPortalFilter, setPendingPortalFilter] = useState<PortalFilter>(portalFilter);
  const [pendingServiceAreaId, setPendingServiceAreaId] = useState<number | ''>(employeeServiceAreaId);
  const [pendingForemanAreaId, setPendingForemanAreaId] = useState<number | ''>(employeeForemanAreaId);
  const [pendingRoleId, setPendingRoleId] = useState<number | ''>(employeeRoleId);
  const [foremanAreas, setForemanAreas] = useState<any[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  
  // Role management state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roleSearchTerm, setRoleSearchTerm] = usePersistedListState(EMPLOYEES_LIST_KEY, 'roles.search', '');
  const [roleSearchInput, setRoleSearchInput] = useState(roleSearchTerm);
  const [roleSortBy, setRoleSortBy] = useState<string | null>(null);
  const [roleSortDir, setRoleSortDir] = useState<'asc' | 'desc'>('asc');
  const [rolePagination, setRolePagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0
  });
  
  // Classifications state
  const [showClassificationModal, setShowClassificationModal] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState<Classification | null>(null);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  // Applied (fetched) vs pending (what the filter panel shows); Apply copies pending -> applied,
  // Clear All resets both (W-M1). The service area used to refetch on every change.
  const [classificationFilters, setClassificationFilters] = usePersistedListState<{ serviceAreaId: number | '' }>(
    EMPLOYEES_LIST_KEY,
    'classifications.filters',
    { serviceAreaId: '' }
  );
  const selectedServiceAreaId = classificationFilters.serviceAreaId;
  const setSelectedServiceAreaId = (serviceAreaId: number | '') => setClassificationFilters({ serviceAreaId });
  const [pendingClassificationServiceAreaId, setPendingClassificationServiceAreaId] = useState<number | ''>(selectedServiceAreaId);
  const [classificationSearchTerm, setClassificationSearchTerm] = usePersistedListState(
    EMPLOYEES_LIST_KEY,
    'classifications.search',
    ''
  );
  const [classificationSearchInput, setClassificationSearchInput] = useState(classificationSearchTerm);
  const [classificationPagination, setClassificationPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [classificationError, setClassificationError] = useState<string | null>(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  
  // Check if user is admin (has user:edit or user:create permissions, or role name is Admin)
  const isAdmin = hasPermission('user:edit') || hasPermission('user:create') || currentUser?.role?.name === 'Admin';
  
  // Classification permissions
  const canCreateClassification = hasPermission('classification:create') || isAdmin;
  const canEditClassification = hasPermission('classification:edit') || isAdmin;

  const canSeeEmployees = hasPermission('user:create') || hasPermission('employee:view');
  const canSeeClassifications = hasPermission('classification:view') || isAdmin;
  const canSeeRoles = hasPermission('role:view');
  const defaultTab: UsersTab = canSeeEmployees
    ? 'employees'
    : canSeeClassifications
      ? 'classifications'
      : 'roles';
  // The tab is in the URL (?tab=); without one, the page reopens on the tab used last (V2-024).
  const [savedTab, setSavedTab] = usePersistedListState(EMPLOYEES_LIST_KEY, 'tab', '');
  const requestedTab = searchParams.get('tab') ?? savedTab;
  const activeTab: UsersTab =
    requestedTab === 'employees' && canSeeEmployees
      ? 'employees'
      : requestedTab === 'classifications' && canSeeClassifications
        ? 'classifications'
        : requestedTab === 'roles' && canSeeRoles
          ? 'roles'
          : defaultTab;

  useEffect(() => {
    setSavedTab(activeTab);
  }, [activeTab]);

  const handleTabSelect = (key: string | null) => {
    const next: UsersTab =
      key === 'employees' || key === 'classifications' || key === 'roles' ? key : defaultTab;
    setActionError(null);
    setSearchParams({ tab: next }, { replace: true });
  };

  const fetchUsers = async (page?: number, resetPage: boolean = false) => {
    try {
      setLoading(true);
      
      const currentPage = resetPage ? 1 : (page || pagination.page);
      
      // Display Inactive off -> status ACTIVE; portal, areas, role and search as applied.
      const filters = {
        ...employeeQueryFilters(employeeFilters, searchTerm),
        ...(userSortBy ? { sortBy: userSortBy, sortOrder: userSortDir } : {})
      };

      // Skip Redis cache to prevent crashes
      const usersData = await usersService.getUsers(currentPage, pagination.limit, filters, true); // skipCache: true
      setUsers(usersData.users);
      setPagination({
        page: usersData.pagination.page,
        limit: usersData.pagination.limit,
        totalCount: usersData.pagination.total,
        totalPages: usersData.pagination.totalPages
      });
      
      // Update master season based on ALL filtered employees' seasons across ALL pages
      // We need to check all filtered users, not just the current page
      // Fetch a larger sample or all users to determine if seasons are mixed
      if (usersData.pagination.total > 0) {
        // Fetch all filtered users (or a large sample) to check their seasons
        const allUsersData = await usersService.getUsers(1, Math.min(usersData.pagination.total, 1000), filters);
        const allSeasons = allUsersData.users.map((user: User) => (user as any).activeSeason || 'summer');
        const uniqueSeasons = Array.from(new Set(allSeasons));
        
        if (uniqueSeasons.length === 1) {
          // All employees across all pages have the same season - set master season to match
          // This allows shift length to display correctly and shows the season in the global filter
          const commonSeason = uniqueSeasons[0] as 'summer' | 'winter';
          setSeason(commonSeason);
          localStorage.setItem('employeeSeasonPreference', commonSeason);
        } else if (uniqueSeasons.length > 1) {
          // Mixed seasons across all pages - ALWAYS reset to null (none selected) so user can choose either
          setSeason(null);
          localStorage.removeItem('employeeSeasonPreference');
        }
      } else {
        // No employees found - reset to null (none selected)
        setSeason(null);
        localStorage.removeItem('employeeSeasonPreference');
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async (page: number = 1, search: string = roleSearchTerm, limit: number = rolePagination.limit) => {
    if (!hasPermission('role:view')) return;

    try {
      const rolesData = await rolesService.getRoles(page, limit, search, roleSortBy || undefined, roleSortDir);
      setRoles(rolesData.roles);
      setRolePagination({
        page: rolesData.pagination.page,
        limit: rolesData.pagination.limit,
        totalCount: rolesData.pagination.total,
        totalPages: rolesData.pagination.totalPages
      });
      const permissionsData = await rolesService.getPermissions();
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  // Fetch filter options for employee tab
  useEffect(() => {
    if (activeTab === 'employees') {
      const fetchFilterOptions = async () => {
        try {
          const [serviceAreasData, foremanAreasData, rolesData] = await Promise.all([
            areasService.getServiceAreas(),
            areasService.getForemanAreas(),
            rolesService.getRoles(1, 1000) // Get all roles
          ]);
          setServiceAreas(serviceAreasData);
          setForemanAreas(foremanAreasData);
          setAllRoles(rolesData.roles);
        } catch (err) {
          console.error('Error fetching filter options:', err);
        }
      };
      fetchFilterOptions();
    }
  }, [activeTab]);

  // Filter roles based on selected service area
  const filteredRoles = useMemo(() => {
    if (!pendingServiceAreaId || !allRoles.length) {
      return allRoles;
    }
    
    // Filter roles that have at least one classification in the selected service area
    return allRoles.filter(role => {
      if (!role.roleClassifications || role.roleClassifications.length === 0) {
        // If role has no classifications, include it (some roles might not have classifications)
        return true;
      }
      // Check if any classification belongs to the selected service area
      return role.roleClassifications.some(rc => 
        rc.classification?.serviceArea?.id === pendingServiceAreaId
      );
    });
  }, [allRoles, pendingServiceAreaId]);

  // Clear role selection if it's no longer available when service area changes
  useEffect(() => {
    // Wait for the roles: a remembered role must not be dropped before the list has loaded.
    if (pendingServiceAreaId && pendingRoleId && allRoles.length > 0) {
      const isRoleAvailable = filteredRoles.some(role => role.id === pendingRoleId);
      if (!isRoleAvailable) {
        setPendingRoleId('');
      }
    }
  }, [pendingServiceAreaId, filteredRoles, pendingRoleId, allRoles.length]);

  // Sync pending filters with applied filters when tab changes
  useEffect(() => {
    if (activeTab === 'employees') {
      setPendingDisplayInactive(displayInactive);
      setPendingPortalFilter(portalFilter);
      setPendingServiceAreaId(employeeServiceAreaId);
      setPendingForemanAreaId(employeeForemanAreaId);
      setPendingRoleId(employeeRoleId);
      setSearchInput(searchTerm);
    }
    if (activeTab === 'classifications') {
      setPendingClassificationServiceAreaId(selectedServiceAreaId);
      setClassificationSearchInput(classificationSearchTerm);
    }
  }, [activeTab]);

  // Fetch users when filters or pagination change
  useEffect(() => {
    if (activeTab === 'employees') {
      // Use fetchUsers to ensure season is updated based on filtered employees
      fetchUsers();
    }
  }, [displayInactive, portalFilter, searchTerm, employeeServiceAreaId, employeeForemanAreaId, employeeRoleId, pagination.page, pagination.limit, activeTab, userSortBy, userSortDir]);

  // Remember the page and page size for the sign-in (V2-024).
  useEffect(() => {
    setSavedEmployeePage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit]);

  // Hidden-match hint (V2-076, SRCH-05): with Display Inactive off, count the same search
  // without the status filter (one row, reading the total) so the page can say how many
  // inactive employees match.
  useEffect(() => {
    if (activeTab !== 'employees' || !searchTerm || displayInactive) {
      setUnfilteredMatchCount(null);
      return;
    }
    let cancelled = false;
    usersService
      .getUsers(1, 1, employeeQueryFilters(employeeFilters, searchTerm, false), true)
      .then((res) => {
        if (!cancelled) setUnfilteredMatchCount(res.pagination.total);
      })
      .catch(() => {
        if (!cancelled) setUnfilteredMatchCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, searchTerm, employeeFilters]);

  const handleUserSort = (key: string, dir: 'asc' | 'desc') => {
    setUserSort({ by: key, dir });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Fetch roles when tab changes or pagination changes
  useEffect(() => {
    if (activeTab === 'roles') {
      fetchRoles(rolePagination.page, roleSearchTerm, rolePagination.limit);
    }
  }, [activeTab, rolePagination.page, rolePagination.limit, roleSearchTerm, roleSortBy, roleSortDir]);

  const handleRoleSort = (key: string, dir: 'asc' | 'desc') => {
    setRoleSortBy(key);
    setRoleSortDir(dir);
    setRolePagination(prev => ({ ...prev, page: 1 }));
  };

  // Initial fetch for roles
  useEffect(() => {
    if (hasPermission('role:view')) {
      fetchRoles(1);
    }
  }, []);

  // Fetch service areas for classifications
  useEffect(() => {
    if (activeTab === 'classifications') {
      fetchServiceAreas();
    }
  }, [activeTab]);

  // Fetch classifications when tab is active or filters change
  useEffect(() => {
    if (activeTab === 'classifications') {
      fetchClassifications();
    }
  }, [activeTab, selectedServiceAreaId, classificationSearchTerm, classificationPagination.page, classificationPagination.limit]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowUserModal(true);
  };

  const handleRowClick = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleSaveUser = async (userData: Partial<User>): Promise<User> => {
    try {
      let savedUser: User;
      if (selectedUser) {
        savedUser = await usersService.updateUser(selectedUser.id, userData);
      } else {
        savedUser = await usersService.createUser(userData);
      }
      await fetchUsers(pagination.page);
      setShowUserModal(false);
      setSelectedUser(null);
      return savedUser;
    } catch (err: any) {
      console.error('Error saving user:', err);
      throw err; // Re-throw so the modal shows the error inline
    }
  };

  const handleViewHistory = (user: User) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
  };

  const handleActivate = async (user: User) => {
    setActionError(null);
    try {
      await usersService.updateUser(user.id, { status: 'ACTIVE' });
      await fetchUsers(pagination.page);
    } catch (err: any) {
      console.error('Error activating user:', err);
      setActionError(describeActionError(err, "Couldn't activate the employee. Try again."));
    }
  };

  const handleDeactivateClick = (user: User) => {
    setDeactivateError(null);
    setUserToDeactivate(user);
  };

  const closeDeactivateModal = () => {
    setUserToDeactivate(null);
    setDeactivateError(null);
  };

  const handleDeactivateConfirm = async () => {
    if (!userToDeactivate) return;
    setDeactivateError(null);
    try {
      await usersService.updateUser(userToDeactivate.id, { status: 'DISABLED' });
      await fetchUsers(pagination.page);
      setUserToDeactivate(null);
    } catch (err: any) {
      console.error('Error deactivating user:', err);
      setDeactivateError(describeActionError(err, "Couldn't deactivate the employee. Try again."));
    }
  };

  const getDeactivateMessage = (user: User) => {
    const parts: string[] = [];
    if (user.hasPortalAccess) parts.push('they will no longer have WRAP access');
    if (user.status === 'ACTIVE') parts.push('they will no longer be available in TrueContext dropdowns');
    if (parts.length === 0) parts.push('they will no longer be active in the system');
    const consequence = parts.join(' or ');
    return `Are you sure you'd like to deactivate this employee? The employee history will remain in the system, but ${consequence}.`;
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
  };

  // Apply filters handler
  const handleApplyFilters = () => {
    const search = searchInput.trim();
    setEmployeeFilters({
      displayInactive: pendingDisplayInactive,
      portal: pendingPortalFilter,
      serviceAreaId: pendingServiceAreaId,
      foremanAreaId: pendingForemanAreaId,
      roleId: pendingRoleId,
    });
    setSearchInput(search);
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Search field (Enter or its clear "×"): runs within the applied filters (SRCH-04).
  const handleSearch = (value: string) => {
    const search = value.trim();
    setSearchInput(search);
    setSearchTerm(search);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Hidden-match hint "Show": turn Display Inactive on, at once.
  const handleShowInactiveMatches = () => {
    setPendingDisplayInactive(true);
    setEmployeeFilters(prev => ({ ...prev, displayInactive: true }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear All: every filter back to its DEFAULT (Display Inactive off) and applied at once.
  const handleResetFilters = () => {
    setPendingDisplayInactive(DEFAULT_EMPLOYEE_FILTERS.displayInactive);
    setPendingPortalFilter('all');
    setPendingServiceAreaId('');
    setPendingForemanAreaId('');
    setPendingRoleId('');
    setSearchInput('');
    setEmployeeFilters(DEFAULT_EMPLOYEE_FILTERS);
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  /**
   * Every applied filter that hides employees, the default Display Inactive off included
   * (V2-076, FLT-05): the chip never reads "No filters applied" while inactive employees are hidden.
   * The search is not counted: it sits beside the chip in the same bar.
   */
  const employeeAppliedFilterCount = [
    !displayInactive,
    portalFilter !== 'all',
    employeeServiceAreaId !== '',
    employeeForemanAreaId !== '',
    employeeRoleId !== '',
  ].filter(Boolean).length;
  /** Anything differs from the defaults: colours the chip amber and enables Clear All. */
  const employeeFiltersChanged =
    displayInactive !== DEFAULT_EMPLOYEE_FILTERS.displayInactive ||
    portalFilter !== 'all' ||
    employeeServiceAreaId !== '' ||
    employeeForemanAreaId !== '' ||
    employeeRoleId !== '' ||
    searchTerm !== '';

  // Check if there are unapplied filter changes
  const hasUnappliedChanges = () => {
    return pendingDisplayInactive !== displayInactive ||
           pendingPortalFilter !== portalFilter ||
           pendingServiceAreaId !== employeeServiceAreaId ||
           pendingForemanAreaId !== employeeForemanAreaId ||
           pendingRoleId !== employeeRoleId;
  };

  // Role handlers
  const handleAddRole = () => {
    setSelectedRole(null);
    setShowRoleModal(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setShowRoleModal(true);
  };

  const handleSaveRole = async (roleData: Partial<Role>) => {
    try {
      if (selectedRole) {
        await rolesService.updateRole(selectedRole.id, roleData);
      } else {
        await rolesService.createRole(roleData);
      }
      await fetchRoles(rolePagination.page);
      setShowRoleModal(false);
    } catch (err) {
      console.error('Error saving role:', err);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if ((await confirmAction({ title: 'Delete Role?', confirmLabel: 'Delete', destructive: true }))) {
      setActionError(null);
      try {
        await rolesService.deleteRole(roleId);
        await fetchRoles(rolePagination.page);
      } catch (err) {
        console.error('Error deleting role:', err);
        setActionError(describeActionError(err, "Couldn't delete the role. Try again."));
      }
    }
  };

  const handleRolePageChange = (page: number) => {
    setRolePagination(prev => ({ ...prev, page }));
  };

  const handleRoleSearch = (value: string) => {
    const search = value.trim();
    setRoleSearchInput(search);
    setRoleSearchTerm(search);
    setRolePagination(prev => ({ ...prev, page: 1 }));
  };

  // Classification handlers
  const fetchServiceAreas = async () => {
    try {
      const areas = await areasService.getServiceAreas();
      setServiceAreas(areas);
    } catch (err) {
      console.error('Error fetching service areas:', err);
    }
  };

  const fetchClassifications = async () => {
    try {
      setClassificationLoading(true);
      setClassificationError(null);
      
      const filters: any = {
        search: classificationSearchTerm || undefined,
        serviceAreaId: selectedServiceAreaId || undefined
      };
      
      const response = await classificationService.getClassifications(classificationPagination.page, classificationPagination.limit, filters);
      setClassifications(response.classifications);
      setClassificationPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching classifications:', err);
      setClassificationError('Failed to fetch classifications');
    } finally {
      setClassificationLoading(false);
    }
  };

  const handleAddClassification = () => {
    setSelectedClassification(null);
    setShowClassificationModal(true);
  };

  const handleEditClassification = (classification: Classification) => {
    setSelectedClassification(classification);
    setShowClassificationModal(true);
  };

  const handleDeleteClassification = async (id: number) => {
    if (!(await confirmAction({ title: 'Delete Classification?', confirmLabel: 'Delete', destructive: true }))) {
      return;
    }

    setActionError(null);
    try {
      await classificationService.deleteClassification(id);
      await fetchClassifications();
    } catch (err: any) {
      console.error('Error deleting classification:', err);
      setActionError(describeActionError(err, "Couldn't delete the classification. Try again."));
    }
  };

  const handleSaveClassification = async (classificationData: Partial<Classification>) => {
    try {
      if (selectedClassification) {
        await classificationService.updateClassification(selectedClassification.id, classificationData);
      } else {
        await classificationService.createClassification(classificationData as { code: string; rate: number; serviceAreaId: number });
      }
      await fetchClassifications();
      setShowClassificationModal(false);
      setSelectedClassification(null);
    } catch (err: any) {
      console.error('Error saving classification:', err);
      throw err; // Re-throw so the modal shows the error inline
    }
  };

  const handleClassificationPageChange = (page: number) => {
    setClassificationPagination(prev => ({ ...prev, page }));
  };

  const handleClassificationPageSizeChange = (pageSize: number) => {
    setClassificationPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
  };

  const hasUnappliedClassificationChanges = pendingClassificationServiceAreaId !== selectedServiceAreaId;
  // The search is not counted in the chip (it sits beside it), but Clear All clears it.
  const classificationActiveFilterCount = selectedServiceAreaId !== '' ? 1 : 0;
  const classificationFiltersChanged = selectedServiceAreaId !== '' || classificationSearchTerm !== '';

  const handleApplyClassificationFilters = () => {
    const search = classificationSearchInput.trim();
    setSelectedServiceAreaId(pendingClassificationServiceAreaId);
    setClassificationSearchInput(search);
    setClassificationSearchTerm(search);
    setClassificationPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClassificationSearch = (value: string) => {
    const search = value.trim();
    setClassificationSearchInput(search);
    setClassificationSearchTerm(search);
    setClassificationPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearClassificationFilters = () => {
    setPendingClassificationServiceAreaId('');
    setSelectedServiceAreaId('');
    setClassificationSearchInput('');
    setClassificationSearchTerm('');
    setClassificationPagination(prev => ({ ...prev, page: 1 }));
  };


  const headerActions: PageAction[] =
    activeTab === 'employees'
      ? [
          {
            key: 'add-employee',
            label: 'Add Employee',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !(hasPermission('user:create') || hasPermission('employee:create')),
            onClick: handleAddUser,
          },
        ]
      : activeTab === 'roles'
        ? [
            {
              key: 'add-role',
              label: 'Add Role',
              icon: 'bi-plus-lg',
              intent: 'primary',
              hidden: !hasPermission('role:create'),
              onClick: handleAddRole,
            },
          ]
        : [
            {
              key: 'add-classification',
              label: 'Add Classification',
              icon: 'bi-plus-lg',
              intent: 'primary',
              hidden: !canCreateClassification,
              onClick: handleAddClassification,
            },
          ];

  const showEmployees = activeTab === 'employees' && canSeeEmployees;
  const showClassifications = activeTab === 'classifications' && canSeeClassifications;
  const showRoles = activeTab === 'roles' && canSeeRoles;

  const actionErrorAlert = actionError && (
    <Alert variant="danger" role="alert" dismissible onClose={() => setActionError(null)} className="tw-shadow-sm tw-border-0">
      {actionError}
    </Alert>
  );

  return (
    // The shared list layout (W-M9, W-H5): the header, the tab bar and the active tab's filters
    // keep their natural height; the active list's table box (ListPage.Body) takes the rest and is
    // the ONE vertical scroll area, with a sticky header – on every tab, Roles included.
    <ListPage className="users-page">
      <PageHeader title={USERS_TAB_TITLES[activeTab]} actions={headerActions} />

      <Tabs
        activeKey={activeTab}
        onSelect={handleTabSelect}
      >
        {canSeeEmployees && (
        <Tab eventKey="employees" title="Employees">
          {/* Filters Card */}
          <FiltersCard
            title="Employee Filters"
            subtitle="Filter employees by portal access, service area, foreman area, and role"
            collapsible
            defaultCollapsed={false}
            onApply={handleApplyFilters}
            applyDisabled={!hasUnappliedChanges()}
            onClear={handleResetFilters}
            hasActiveFilters={employeeFiltersChanged}
            appliedFilterCount={employeeAppliedFilterCount}
            search={{
              value: searchInput,
              onChange: setSearchInput,
              onSearch: handleSearch,
              placeholder: 'Name, email, or employee number',
              label: 'Search employees',
            }}
          >
            {/* Single selects: GET /api/users parses serviceAreaId, foremanAreaId, roleId and
                status as one value each (server user.controller.ts), so none can be a list. */}
            <FilterGrid>
              <FilterField>
                <Form.Label htmlFor="users-filter-service-area">Service Area</Form.Label>
                <Form.Select
                  id="users-filter-service-area"
                  value={pendingServiceAreaId}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : '';
                    setPendingServiceAreaId(value);
                    // Clear foreman area if service area changes
                    if (value !== pendingServiceAreaId) {
                      setPendingForemanAreaId('');
                    }
                  }}
                >
                  <option value="">All Service Areas</option>
                  {serviceAreas.map(sa => (
                    <option key={sa.id} value={sa.id}>
                      {sa.name}
                    </option>
                  ))}
                </Form.Select>
              </FilterField>
              <FilterField>
                <Form.Label htmlFor="users-filter-foreman-area">Foreman Area</Form.Label>
                <Form.Select
                  id="users-filter-foreman-area"
                  value={pendingForemanAreaId}
                  onChange={(e) => setPendingForemanAreaId(e.target.value ? parseInt(e.target.value) : '')}
                >
                  <option value="">All Foreman Areas</option>
                  {foremanAreas
                    .filter(fa => !pendingServiceAreaId || fa.serviceAreaId === pendingServiceAreaId)
                    .map(fa => (
                      <option key={fa.id} value={fa.id}>
                        {fa.name}
                      </option>
                    ))}
                </Form.Select>
              </FilterField>
              <FilterField>
                <Form.Label htmlFor="users-filter-role">Role</Form.Label>
                <Form.Select
                  id="users-filter-role"
                  value={pendingRoleId}
                  onChange={(e) => setPendingRoleId(e.target.value ? parseInt(e.target.value) : '')}
                >
                  <option value="">All Roles</option>
                  {filteredRoles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Form.Select>
              </FilterField>
              <FilterField>
                <Form.Label htmlFor="users-filter-portal">Portal Access</Form.Label>
                <Form.Select
                  id="users-filter-portal"
                  value={pendingPortalFilter}
                  onChange={(e) => setPendingPortalFilter(e.target.value as PortalFilter)}
                >
                  <option value="all">All</option>
                  <option value="portal">Portal Access</option>
                  <option value="without-portal">No Portal Access</option>
                </Form.Select>
              </FilterField>
              {/* Employees are simply active or inactive (V2-075): a toggle, not a Status dropdown. */}
              <FilterField>
                <DisplayInactiveToggle
                  id="users-filter-display-inactive"
                  checked={pendingDisplayInactive}
                  onChange={setPendingDisplayInactive}
                />
              </FilterField>
            </FilterGrid>
            {/* Season is an action on the filtered employees (it asks for confirmation), not a
                filter, so it stays outside the Apply / Clear All model. */}
            <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <span className="tw-text-sm tw-font-medium tw-text-content-secondary">Season</span>
              <ButtonGroup>
                <Button
                  size="sm"
                  variant={season === 'summer' ? 'warning' : 'outline-warning'}
                  onClick={() => handleSeasonChange('summer')}
                  style={{
                    minWidth: '100px',
                    fontWeight: season === 'summer' ? '600' : '400'
                  }}
                >
                  <i className="bi bi-sun tw-mr-1"></i>
                  Summer
                </Button>
                <Button
                  size="sm"
                  variant={season === 'winter' ? 'primary' : 'outline-primary'}
                  onClick={() => handleSeasonChange('winter')}
                  style={{
                    minWidth: '100px',
                    fontWeight: season === 'winter' ? '600' : '400'
                  }}
                >
                  <i className="bi bi-snow tw-mr-1"></i>
                  Winter
                </Button>
              </ButtonGroup>
              {season && (
                <Badge
                  bg={season === 'summer' ? 'warning' : 'primary'}
                  text="white"
                  className="tw-px-3 tw-py-2"
                  style={{ fontSize: '0.9em' }}
                >
                  <i className={`bi ${season === 'summer' ? 'bi-sun' : 'bi-snow'} tw-mr-1`}></i>
                  {season === 'summer' ? 'Summer' : 'Winter'} Shift Length
                </Badge>
              )}
            </div>
          </FiltersCard>

          {unfilteredMatchCount !== null && !loading && (
            <HiddenMatchHint
              search={searchTerm}
              shownCount={pagination.totalCount}
              unfilteredCount={unfilteredMatchCount}
              shownNoun="active employees"
              hiddenNoun={['inactive match', 'inactive matches']}
              onShow={handleShowInactiveMatches}
            />
          )}

          {error && (
            <Alert variant="danger" className="tw-shadow-sm tw-border-0">
              <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
              {error}
            </Alert>
          )}
          {actionErrorAlert}
        </Tab>
        )}

        {canSeeClassifications && (
          <Tab eventKey="classifications" title="Classifications & Rates">
            <FiltersCard
              title="Classification Filters"
              subtitle="Search and filter classifications by service area"
              collapsible
              defaultCollapsed={false}
              onApply={handleApplyClassificationFilters}
              applyDisabled={!hasUnappliedClassificationChanges}
              onClear={handleClearClassificationFilters}
              hasActiveFilters={classificationFiltersChanged}
              appliedFilterCount={classificationActiveFilterCount}
              search={{
                value: classificationSearchInput,
                onChange: setClassificationSearchInput,
                onSearch: handleClassificationSearch,
                placeholder: 'Classification code',
                label: 'Search classifications',
              }}
            >
              <FilterGrid>
                <FilterField>
                  {/* Single select: GET /api/classifications parses serviceAreaId with parseInt
                      (server classification.controller.ts). */}
                  <Form.Label htmlFor="users-classification-service-area">Service Area</Form.Label>
                  <Form.Select
                    id="users-classification-service-area"
                    value={pendingClassificationServiceAreaId}
                    onChange={(e) =>
                      setPendingClassificationServiceAreaId(e.target.value ? parseInt(e.target.value) : '')
                    }
                  >
                    <option value="">All Service Areas</option>
                    {serviceAreas.map(sa => (
                      <option key={sa.id} value={sa.id}>
                        {sa.name}
                      </option>
                    ))}
                  </Form.Select>
                </FilterField>
              </FilterGrid>
            </FiltersCard>

            {classificationError && (
              <Alert variant="danger" className="tw-shadow-sm tw-border-0">
                <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                {classificationError}
              </Alert>
            )}
            {actionErrorAlert}
          </Tab>
        )}

        {canSeeRoles && (
          <Tab eventKey="roles" title="Roles & Permissions">
            {/* Roles have no filters, only the search, so the panel is just its header bar
                (V2-077: the search lives here, not behind a header Search button). */}
            <FiltersCard
              title="Role Filters"
              bodyClassName="tw-hidden"
              // No body: drop the header's bottom rule so the bar does not end in a double line.
              className="[&>[data-slot=card-header]]:tw-border-b-0"
              onClear={() => handleRoleSearch('')}
              hasActiveFilters={roleSearchTerm !== ''}
              appliedFilterCount={0}
              search={{
                value: roleSearchInput,
                onChange: setRoleSearchInput,
                onSearch: handleRoleSearch,
                placeholder: 'Role name',
                label: 'Search roles',
              }}
            >
              {null}
            </FiltersCard>
            {actionErrorAlert}
          </Tab>
        )}
      </Tabs>

      {showEmployees && (
        <>
          <ListPage.Body data-testid="users-list-body">
            {loading ? (
              <div className="tw-text-center tw-py-5">
                <Spinner className="tw-text-primary" style={{ width: '3rem', height: '3rem' }}>Loading...</Spinner>
                <p className="tw-text-content-muted tw-mt-3">Loading employees...</p>
              </div>
            ) : (
              <UsersTable
                users={users}
                onRowClick={handleRowClick}
                onViewHistory={handleViewHistory}
                onActivate={handleActivate}
                onDeactivate={handleDeactivateClick}
                onSeasonChange={handleUserSeasonChange}
                season={season}
                sortBy={userSortBy}
                sortDir={userSortDir}
                onSort={handleUserSort}
              />
            )}
          </ListPage.Body>
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.limit}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
            className="tw-mt-2"
          />
        </>
      )}

      {showRoles && (
        <>
          <ListPage.Body data-testid="roles-list-body">
            <TableScroll>
              <RolesTable
                roles={roles}
                permissions={permissions}
                onEditRole={handleEditRole}
                onDeleteRole={handleDeleteRole}
                sortBy={roleSortBy}
                sortDir={roleSortDir}
                onSort={handleRoleSort}
              />
            </TableScroll>
          </ListPage.Body>
          {rolePagination.totalPages > 1 && (
            <div className="tw-mt-3">
              <ServerPagination
                currentPage={rolePagination.page}
                totalPages={rolePagination.totalPages}
                totalCount={rolePagination.totalCount}
                pageSize={rolePagination.limit}
                onPageChange={handleRolePageChange}
                onPageSizeChange={(size) => setRolePagination(prev => ({ ...prev, limit: size, page: 1 }))}
                loading={false}
              />
            </div>
          )}
        </>
      )}

      {showClassifications && (
        <>
          <ListPage.Body>
            <TableScroll>
              <Table hover stickyHeader className="tw-mb-0">
                <thead>
                  <tr>
                    <th className="wrap-pin-left wrap-pin-edge">Code</th>
                    <th>Service Area</th>
                    <th>Rate</th>
                    <th style={{ minWidth: '16rem' }}>Roles</th>
                    <th style={{ width: ACTIONS_COL_WIDTH }} className="wrap-pin-right tw-text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {classificationLoading ? (
                    <tr>
                      <td colSpan={5} className="tw-text-center tw-py-5">
                        <Spinner className="tw-text-primary">Loading...</Spinner>
                        <p className="tw-text-content-muted tw-mt-3 tw-mb-0">Loading classifications...</p>
                      </td>
                    </tr>
                  ) : classifications.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="tw-text-center tw-py-4">
                        <i className="bi bi-tags tw-text-6xl tw-font-light tw-text-content-muted"></i>
                        <h5 className="tw-mt-3">No classifications found</h5>
                        <p className="tw-text-content-muted">
                          {selectedServiceAreaId || classificationSearchTerm
                            ? 'Try adjusting your filters or search terms.'
                            : 'Create your first classification to get started.'}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    classifications.map((classification) => (
                      <tr key={classification.id}>
                        <td className="wrap-pin-left wrap-pin-edge">
                          <Badge bg="secondary" text="white">
                            {classification.code}
                          </Badge>
                        </td>
                        <td>
                          {classification.serviceArea?.name || 'Unknown'}
                        </td>
                        <td>
                          <strong>${classification.rate.toFixed(2)}</strong>
                        </td>
                        <td style={{ minWidth: '16rem' }}>
                          {classification.roleClassifications && classification.roleClassifications.length > 0 ? (
                            <div className="tw-flex tw-flex-wrap tw-gap-1">
                              {classification.roleClassifications.map((rc) => (
                                <Badge key={rc.id} bg="info" text="dark">
                                  {rc.role.name}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <EmptyValue />
                          )}
                        </td>
                        <td className="wrap-pin-right tw-text-right">
                          <RowActions
                            actions={[
                              {
                                kind: 'edit',
                                label: 'Edit Classification',
                                onClick: () => handleEditClassification(classification),
                                hidden: !canEditClassification,
                              },
                              {
                                kind: 'delete',
                                label: 'Delete Classification',
                                onClick: () => handleDeleteClassification(classification.id),
                                hidden: !canEditClassification,
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </TableScroll>
          </ListPage.Body>
          <ServerPagination
            currentPage={classificationPagination.page}
            totalPages={classificationPagination.totalPages}
            totalCount={classificationPagination.total}
            pageSize={classificationPagination.limit}
            onPageChange={handleClassificationPageChange}
            onPageSizeChange={handleClassificationPageSizeChange}
            loading={classificationLoading}
            className="tw-mt-2"
          />
        </>
      )}

      {/* Deactivate confirmation modal */}
      <Modal show={!!userToDeactivate} onHide={closeDeactivateModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Employee</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deactivateError && (
            <Alert variant="danger" role="alert" className="tw-mb-3">
              {deactivateError}
            </Alert>
          )}
          {userToDeactivate && getDeactivateMessage(userToDeactivate)}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeactivateModal}>
            No
          </Button>
          <Button variant="primary" onClick={handleDeactivateConfirm}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Classification Modal */}
      <ClassificationModal
        show={showClassificationModal}
        onHide={() => {
          setShowClassificationModal(false);
          setSelectedClassification(null);
        }}
        classification={selectedClassification}
        serviceAreas={serviceAreas}
        onSave={handleSaveClassification}
      />

      {/* User Modal */}
      <UserModal 
        show={showUserModal} 
        onHide={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUser}
      />

      {/* User History Modal */}
      {selectedUser && (
        <UserHistoryModal
          visible={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedUser(null);
          }}
          userId={selectedUser.id}
          userName={`${selectedUser.name} ${selectedUser.lastName}`}
        />
      )}

      {/* Role Modal */}
      {hasPermission('role:create') && (
        <RoleModal
          show={showRoleModal}
          onHide={() => setShowRoleModal(false)}
          role={selectedRole}
          permissions={permissions}
          onSave={handleSaveRole}
        />
      )}

      {/* Season Change Confirmation Modal */}
      <Modal show={showSeasonConfirm} onHide={cancelSeasonChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill tw-text-warning-text tw-mr-2"></i>
            Confirm Bulk Season Change
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            All employees within the current filter will be affected across <strong>all pages</strong>.
          </p>
          <p>
            <strong>{bulkUpdateCount} employee{bulkUpdateCount !== 1 ? 's' : ''}</strong> across all pages will be switched to <strong>{pendingSeason === 'summer' ? 'Summer' : 'Winter'}</strong>.
          </p>
          <p className="tw-text-content-muted tw-mb-0">
            <i className="bi bi-info-circle tw-mr-1"></i>
            This will update all matching employees, not just those visible on the current page. Are you sure you want to proceed?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelSeasonChange}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSeasonChange}>
            Confirm Change
          </Button>
        </Modal.Footer>
      </Modal>
    </ListPage>
  );
};

export default UsersPage;
